import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import {
  ArrowLeft,
  Search,
  Plus,
  Minus,
  User,
  Wrench,
  Package,
  Bike,
  X,
  Pencil,
  Bell,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-format registration number with dashes
const formatRegistrationNumber = (value) => {
  // Remove all non-alphanumeric characters
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  
  // Check if it starts with letters (LA/HA format) or numbers (60-4240 format)
  const startsWithLetters = /^[A-Z]/.test(cleaned);
  
  if (startsWithLetters) {
    // Format: LA-60-4240 or HA-60-4240
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    } else {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 8)}`;
    }
  } else {
    // Format: 60-4240
    if (cleaned.length <= 2) {
      return cleaned;
    } else {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}`;
    }
  }
};

export default function JobCreatePage() {
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const [searchParams] = useSearchParams();
  const preSelectedCustomerId = searchParams.get('customer_id');
  const { t, language } = useLanguage();
  const { toast } = useToast();
  
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [parts, setParts] = useState([]);
  const [recentParts, setRecentParts] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [editingPartPrice, setEditingPartPrice] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [editingServicePrice, setEditingServicePrice] = useState(null);
  const [customService, setCustomService] = useState({ name: '', price: '', cost: '' });
  const [showServiceCost, setShowServiceCost] = useState(false);
  const [customPart, setCustomPart] = useState({ name: '', selling_price: '', buying_price: '' });
  const [technicians, setTechnicians] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralSuggestions, setReferralSuggestions] = useState([]);
  const [showReferralSuggestions, setShowReferralSuggestions] = useState(false);
  const [partCategoryMap, setPartCategoryMap] = useState({});
  
  // Selected customer's bikes
  const [customerBikes, setCustomerBikes] = useState([]);
  const [selectedBikeId, setSelectedBikeId] = useState('');
  const [showNewBike, setShowNewBike] = useState(false);
  const [bikeFormAttempted, setBikeFormAttempted] = useState(false);

  // Customer dues
  const [customerDues, setCustomerDues] = useState(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    bike_id: '',
    bike_make: '',
    bike_model: '',
    bike_year: '',
    bike_registration: '',
    bike_color: '',
    services: [],
    service_prices: {},
    custom_services: [],
    parts_used: [],
    custom_parts: [],
    technician_id: '',
    notes: '',
    expected_delivery: ''
  });

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    address: '',
    referred_by_customer_id: ''
  });
  const [newCustomerPhoneError, setNewCustomerPhoneError] = useState('');

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 11) {
      return language === 'en' ? 'Phone number must be 11 digits' : 'ফোন নম্বর অবশ্যই ১১ ডিজিট হতে হবে';
    }
    if (!/^01/.test(digits)) {
      return language === 'en' ? 'Phone number must start with 01' : 'ফোন নম্বর 01 দিয়ে শুরু হতে হবে';
    }
    return '';
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load pre-selected customer if customer_id is in URL
  useEffect(() => {
    if (preSelectedCustomerId) {
      loadPreSelectedCustomer(preSelectedCustomerId);
    }
  }, [preSelectedCustomerId]);

  const loadPreSelectedCustomer = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}`);
      const customer = response.data;
      setFormData(prev => ({ ...prev, customer_id: customer.id }));
      setSelectedCustomer(customer);
      setCustomerSearch(customer.name + ' - ' + customer.phone);
      // Load customer's bikes
      await fetchCustomerBikes(customer.id);
    } catch (error) {
      console.error('Failed to load customer:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [servicesRes, usersRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/users`)
      ]);
      setServices(servicesRes.data);
      setTechnicians(usersRes.data.filter(u => u.role === 'technician'));
      // Load recent parts and services from localStorage
      loadRecentParts();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const loadRecentParts = async () => {
    try {
      const recentPartIds = JSON.parse(localStorage.getItem('recentParts') || '[]');
      if (recentPartIds.length > 0) {
        const response = await axios.post(`${API}/parts/batch`, recentPartIds.slice(0, 8));
        const recent = recentPartIds
          .map(id => response.data.find(p => p.id === id))
          .filter(p => p && p.stock_quantity > 0)
          .slice(0, 8);
        setRecentParts(recent);
      }
    } catch (error) {
      console.error('Failed to load recent parts:', error);
    }
  };

  const fetchParts = async (searchTerm) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('limit', '24');
      const response = await axios.get(`${API}/parts?${params}`);
      setParts(response.data.filter(p => p.stock_quantity > 0));
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    }
  };

  const fetchServices = async (searchTerm) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const response = await axios.get(`${API}/services?${params}`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCustomerBikes = async (customerId) => {
    try {
      const response = await axios.get(`${API}/bikes?customer_id=${customerId}`);
      setCustomerBikes(response.data || []);
      // Auto-select if only one bike
      if (response.data && response.data.length === 1) {
        selectBike(response.data[0]);
      } else {
        // Reset bike selection when switching customers
        setSelectedBikeId('');
        setFormData(prev => ({
          ...prev,
          bike_id: '',
          bike_make: '',
          bike_model: '',
          bike_registration: ''
        }));
        setShowNewBike(response.data?.length === 0);
      }
    } catch (error) {
      console.error('Failed to fetch bikes:', error);
      setCustomerBikes([]);
    }
    // Fetch customer dues (non-blocking)
    axios.get(`${API}/invoices/dues/customer/${customerId}`)
      .then(r => setCustomerDues(r.data))
      .catch(() => setCustomerDues(null));
  };

  const searchCustomers = async (query) => {
    if (query.length < 2) return;
    try {
      const response = await axios.get(`${API}/customers?search=${query}`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  };

  const handleCustomerSearch = (value) => {
    setCustomerSearch(value);
    searchCustomers(value);
  };

  const selectCustomer = async (customer) => {
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name + ' - ' + customer.phone);
    setCustomers([]);
    // Load customer's bikes
    await fetchCustomerBikes(customer.id);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setFormData(prev => ({ ...prev, customer_id: '', bike_id: '', bike_make: '', bike_model: '', bike_year: '', bike_registration: '', bike_color: '' }));
    setCustomerBikes([]);
    setSelectedBikeId('');
    setShowNewBike(false);
    setCustomerDues(null);
  };

  const selectBike = (bike) => {
    setSelectedBikeId(bike.id);
    setFormData(prev => ({
      ...prev,
      bike_id: bike.id,
      bike_make: bike.make,
      bike_model: bike.model,
      bike_year: bike.year || '',
      bike_registration: bike.registration_number || '',
      bike_color: bike.color || ''
    }));
    setShowNewBike(false);
  };

  const handleNewBikeSelect = () => {
    setSelectedBikeId('new');
    setShowNewBike(true);
    setFormData(prev => ({
      ...prev,
      bike_id: '',
      bike_make: '',
      bike_model: '',
      bike_year: '',
      bike_registration: '',
      bike_color: ''
    }));
  };

  const createNewCustomer = async () => {
    if (!newCustomer.phone) {
      setNewCustomerPhoneError(language === 'en' ? 'Phone number is required' : 'ফোন নম্বর প্রয়োজন');
      return;
    }
    const phoneErr = validatePhone(newCustomer.phone);
    if (phoneErr) {
      setNewCustomerPhoneError(phoneErr);
      toast({ title: phoneErr, variant: 'destructive' });
      return;
    }
    try {
      const response = await axios.post(`${API}/customers`, newCustomer);
      setFormData(prev => ({ ...prev, customer_id: response.data.id }));
      setSelectedCustomer(response.data);
      setCustomerSearch(response.data.name + ' - ' + response.data.phone);
      setShowNewCustomer(false);
      setReferralSearch('');
      setReferralSuggestions([]);
      setShowReferralSuggestions(false);
      setCustomerBikes([]);
      setShowNewBike(true); // New customer needs new bike
      toast({ title: language === 'en' ? 'Customer created!' : 'গ্রাহক তৈরি হয়েছে!' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to create customer' : 'গ্রাহক তৈরি ব্যর্থ', variant: 'destructive' });
    }
  };

  const searchReferralCustomers = async (query) => {
    if (query.length < 2) {
      setReferralSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/customers?search=${encodeURIComponent(query)}&limit=10`);
      const list = (response.data || []).filter(c => c.id !== formData.customer_id && c.phone !== newCustomer.phone).slice(0, 10);
      setReferralSuggestions(list);
    } catch (error) {
      setReferralSuggestions([]);
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
    
    // Clear search and reload all services when selecting
    if (!formData.services.includes(serviceId)) {
      setServiceSearch('');
      fetchServices('');
    }
  };

  const updateServicePrice = (serviceId, newPrice) => {
    const priceNum = Math.max(0, parseFloat(newPrice) || 0);
    setFormData(prev => ({
      ...prev,
      service_prices: {
        ...prev.service_prices,
        [serviceId]: priceNum
      }
    }));
  };

  const addCustomService = () => {
    if (!customService.name || !customService.price) {
      toast({ title: language === 'en' ? 'Please enter service name and price' : 'সার্ভিসের নাম এবং মূল্য লিখুন', variant: 'destructive' });
      return;
    }

    const customId = `custom_${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      custom_services: [...(prev.custom_services || []), {
        id: customId,
        name: customService.name,
        price: parseFloat(customService.price) || 0,
        cost: parseFloat(customService.cost) || 0
      }]
    }));

    setCustomService({ name: '', price: '', cost: '' });
  };

  const removeCustomService = (customId) => {
    setFormData(prev => ({
      ...prev,
      custom_services: (prev.custom_services || []).filter(s => s.id !== customId)
    }));
  };

  const addCustomPart = () => {
    if (!customPart.name || !customPart.selling_price) {
      toast({ title: language === 'en' ? 'Part name and selling price required' : 'পার্টসের নাম ও বিক্রয় মূল্য আবশ্যক', variant: 'destructive' });
      return;
    }
    const cpId = `cp_${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      custom_parts: [...(prev.custom_parts || []), {
        id: cpId,
        name: customPart.name,
        quantity: 1,
        selling_price: parseFloat(customPart.selling_price) || 0,
        buying_price: parseFloat(customPart.buying_price) || 0
      }]
    }));
    setCustomPart({ name: '', selling_price: '', buying_price: '' });
  };

  const removeCustomPart = (cpId) => {
    setFormData(prev => ({
      ...prev,
      custom_parts: (prev.custom_parts || []).filter(p => p.id !== cpId)
    }));
  };

  const updateCustomPartQuantity = (cpId, delta) => {
    setFormData(prev => ({
      ...prev,
      custom_parts: (prev.custom_parts || [])
        .map(p => p.id === cpId ? { ...p, quantity: Math.max(0, (p.quantity || 1) + delta) } : p)
        .filter(p => p.quantity > 0)
    }));
  };

  // Fetch consumable category mappings for reminder indicator
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/marketing/part-category-map`);
        setPartCategoryMap(data || {});
      } catch { /* silent */ }
    })();
  }, []);

  const addPart = (part) => {
    const existing = formData.parts_used.find(p => p.part_id === part.id);
    if (existing) {
      if (existing.quantity < part.stock_quantity) {
        setFormData(prev => ({
          ...prev,
          parts_used: prev.parts_used.map(p =>
            p.part_id === part.id ? { ...p, quantity: p.quantity + 1 } : p
          )
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        parts_used: [...prev.parts_used, {
          part_id: part.id,
          part_name: part.name,
          quantity: 1,
          unit_price: part.selling_price
        }]
      }));
    }

    // Save to recent parts
    const recentPartIds = JSON.parse(localStorage.getItem('recentParts') || '[]');
    const updatedRecent = [part.id, ...recentPartIds.filter(id => id !== part.id)].slice(0, 10);
    localStorage.setItem('recentParts', JSON.stringify(updatedRecent));
    loadRecentParts();
    
    // Clear search
    setPartSearch('');
  };

  const updatePartQuantity = (partId, delta) => {
    setFormData(prev => ({
      ...prev,
      parts_used: prev.parts_used
        .map(p => p.part_id === partId ? { ...p, quantity: Math.max(0, p.quantity + delta) } : p)
        .filter(p => p.quantity > 0)
    }));
  };

  const updatePartPrice = (partId, totalPrice) => {
    setFormData(prev => ({
      ...prev,
      parts_used: prev.parts_used.map(p => {
        if (p.part_id === partId) {
          const total = Math.max(0, parseFloat(totalPrice) || 0);
          const unit_price = p.quantity > 0 ? total / p.quantity : 0;
          return { ...p, unit_price };
        }
        return p;
      })
    }));
  };

  const calculateTotal = () => {
    const servicesTotal = formData.services.reduce((sum, id) => {
      const service = services.find(s => s.id === id);
      const price = formData.service_prices?.[id] ?? service?.price ?? 0;
      return sum + price;
    }, 0);
    const customServicesTotal = (formData.custom_services || []).reduce((sum, s) => sum + s.price, 0);
    const partsTotal = formData.parts_used.reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
    const customPartsTotal = (formData.custom_parts || []).reduce((sum, p) => sum + (p.quantity * p.selling_price), 0);
    return { servicesTotal: servicesTotal + customServicesTotal, partsTotal: partsTotal + customPartsTotal, total: servicesTotal + customServicesTotal + partsTotal + customPartsTotal };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      // If adding a new bike, create it first
      let bikeId = formData.bike_id;
      if (showNewBike && formData.customer_id) {
        if (!formData.bike_make || !formData.bike_model) {
          setBikeFormAttempted(true);
          toast({ title: language === 'en' ? 'Vehicle make and model are required' : 'বাইক মেক এবং মডেল প্রয়োজন', variant: 'destructive' });
          setLoading(false);
          return;
        }
        setBikeFormAttempted(false);
        try {
          const bikeResponse = await axios.post(`${API}/bikes`, {
            customer_id: formData.customer_id,
            make: formData.bike_make,
            model: formData.bike_model,
            year: formData.bike_year ? parseInt(formData.bike_year) : null,
            registration_number: formData.bike_registration || null,
            color: formData.bike_color || null
          });
          bikeId = bikeResponse.data.id;
        } catch (bikeError) {
          console.error('Failed to create bike:', bikeError);
          // Continue anyway, bike info will be in job
        }
      }

      const jobData = {
        ...formData,
        bike_id: bikeId || null
      };

      const response = await axios.post(`${API}/jobs`, jobData);
      const reminders = response.data.queued_reminders || [];
      if (reminders.length > 0) {
        const lines = reminders.map(r => `${r.target_name} → ${r.interval_days} days (${new Date(r.remind_date).toLocaleDateString()})`).join('\n');
        toast({
          title: language === 'en' ? 'Job created! Reminders queued:' : 'জব তৈরি! রিমাইন্ডার সেট হয়েছে:',
          description: lines,
          duration: 8000
        });
      } else {
        toast({ title: language === 'en' ? 'Job created successfully!' : 'জব সফলভাবে তৈরি হয়েছে!' });
      }
      navigate(`${basePath}/jobs/${response.data.id}`);
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to create job' : 'জব তৈরি ব্যর্থ', 
        description: error.response?.data?.detail,
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotal();

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(`${basePath}/jobs`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
              {t('create_job')}
            </h1>
            <p className="text-slate-500">
              {language === 'en' ? 'Create a new service job' : 'নতুন সার্ভিস জব তৈরি করুন'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer & Bike Info - Combined */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {language === 'en' ? 'Customer & Vehicle Information' : 'গ্রাহক এবং বাইকের তথ্য'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer Section */}
              {!selectedCustomer ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder={language === 'en' ? 'Search customer by name or phone...' : 'নাম বা ফোন দিয়ে গ্রাহক খুঁজুন...'}
                      value={customerSearch}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      className="pl-10 h-12"
                      data-testid="customer-search"
                    />
                    {customers.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-auto">
                        {customers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => selectCustomer(customer)}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-b-0"
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-slate-500">{customer.phone}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewCustomer(!showNewCustomer)}
                    className="w-full"
                    data-testid="new-customer-toggle"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Add New Customer' : 'নতুন গ্রাহক যোগ করুন'}
                  </Button>

                  {showNewCustomer && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                      <Input
                        placeholder={language === 'en' ? 'Customer Name' : 'গ্রাহকের নাম'}
                        value={newCustomer.name}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="new-customer-name"
                      />
                      <Input
                        placeholder={language === 'en' ? 'Phone Number (01XXXXXXXXX)' : 'ফোন নম্বর (01XXXXXXXXX)'}
                        value={newCustomer.phone}
                        onChange={(e) => {
                          setNewCustomer(prev => ({ ...prev, phone: e.target.value }));
                          if (e.target.value) setNewCustomerPhoneError(validatePhone(e.target.value));
                          else setNewCustomerPhoneError('');
                        }}
                        maxLength={11}
                        className={newCustomerPhoneError ? 'border-red-500 ring-1 ring-red-500' : ''}
                        data-testid="new-customer-phone"
                      />
                      {newCustomerPhoneError && <p className="text-xs text-red-500 -mt-1">{newCustomerPhoneError}</p>}
                      <Input
                        placeholder={language === 'en' ? 'Address (optional)' : 'ঠিকানা (ঐচ্ছিক)'}
                        value={newCustomer.address}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                      />

                      <div>
                        <Label className="text-xs">{t('marketing_referred_by')} ({language === 'en' ? 'optional' : 'ঐচ্ছিক'})</Label>
                        <Input
                          placeholder={language === 'en' ? 'Search existing customer...' : 'বিদ্যমান গ্রাহক খুঁজুন...'}
                          value={referralSearch}
                          onFocus={() => setShowReferralSuggestions(true)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setReferralSearch(value);
                            setShowReferralSuggestions(true);
                            setNewCustomer(prev => ({ ...prev, referred_by_customer_id: '' }));
                            searchReferralCustomers(value);
                          }}
                          className="h-10"
                        />

                        {newCustomer.referred_by_customer_id && (
                          <p className="text-xs text-blue-700 mt-1">
                            {language === 'en' ? 'Referral selected' : 'রেফারেল নির্বাচন করা হয়েছে'}
                          </p>
                        )}

                        {showReferralSuggestions && referralSearch.length >= 2 && (
                          <div className="border rounded-md max-h-40 overflow-y-auto mt-1 bg-white">
                            {referralSuggestions.length > 0 ? referralSuggestions.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                                onClick={() => {
                                  setNewCustomer(prev => ({ ...prev, referred_by_customer_id: c.id }));
                                  setReferralSearch(`${c.name} - ${c.phone}`);
                                  setShowReferralSuggestions(false);
                                }}
                              >
                                {c.name} - {c.phone}
                              </button>
                            )) : (
                              <p className="px-3 py-2 text-xs text-slate-500">{language === 'en' ? 'No matching customer found' : 'মিলে এমন কোন গ্রাহক নেই'}</p>
                            )}
                          </div>
                        )}

                        {newCustomer.referred_by_customer_id && (
                          <button
                            type="button"
                            className="text-xs text-red-500 mt-1"
                            onClick={() => {
                              setNewCustomer(prev => ({ ...prev, referred_by_customer_id: '' }));
                              setReferralSearch('');
                              setReferralSuggestions([]);
                              setShowReferralSuggestions(false);
                            }}
                          >
                            {language === 'en' ? 'Clear referral' : 'রেফারেল মুছুন'}
                          </button>
                        )}
                      </div>

                      <Button type="button" onClick={createNewCustomer} className="w-full" data-testid="create-customer-btn">
                        {language === 'en' ? 'Create Customer' : 'গ্রাহক তৈরি করুন'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Selected Customer Display */}
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-blue-800" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{selectedCustomer.name}</p>
                          <p className="text-sm text-slate-500">{selectedCustomer.phone}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearCustomer}
                        className="p-1 hover:bg-red-100 rounded transition-colors"
                        data-testid="clear-customer"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Previous Dues Warning */}
                  {customerDues && customerDues.total_due > 0 && (
                    <a
                      href={`${basePath}/customers/${selectedCustomer?.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-700">
                            {language === 'en' ? 'Previous Due' : 'পূর্ববর্তী বকেয়া'}: {formatCurrency(customerDues.total_due, language)}
                          </p>
                          <p className="text-xs text-red-600">
                            {customerDues.total_invoices} {language === 'en' ? 'unpaid invoice(s)' : 'টি অপরিশোধিত ইনভয়েস'}
                          </p>
                        </div>
                      </div>
                    </a>
                  )}

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">
                        <Bike className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Bike Section - Only show if customer is selected */}
              {selectedCustomer && (
                <>
                  {/* Show bike selection if customer has bikes */}
                  {customerBikes.length > 0 && (
                    <div className="space-y-3">
                      <Label>{language === 'en' ? 'Select Vehicle' : 'বাইক নির্বাচন করুন'}</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {customerBikes.map((bike) => (
                          <button
                            key={bike.id}
                            type="button"
                            onClick={() => selectBike(bike)}
                            className={`text-left p-3 rounded-lg border transition-colors ${
                              selectedBikeId === bike.id 
                                ? 'border-primary bg-primary/10' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`bike-option-${bike.id}`}
                          >
                            <p className="font-medium">{bike.make} {bike.model}</p>
                            <div className="flex gap-2 text-sm text-slate-500">
                              {bike.year && <span>{bike.year}</span>}
                              {bike.registration_number && <span>{bike.year ? '•' : ''} {bike.registration_number}</span>}
                              {bike.color && <span>{(bike.year || bike.registration_number) ? '•' : ''} {bike.color}</span>}
                            </div>
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={handleNewBikeSelect}
                          className={`text-left p-3 rounded-lg border transition-colors ${
                            showNewBike 
                              ? 'border-primary bg-primary/10' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid="add-new-bike-option"
                        >
                          <p className="font-medium flex items-center justify-center gap-2">
                            <Plus className="w-4 h-4" />
                            {language === 'en' ? 'Add New Vehicle' : 'নতুন বাইক যোগ করুন'}
                          </p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show new bike form if no bikes or adding new */}
                  {(customerBikes.length === 0 || showNewBike) && (
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Make' : 'মেক'} *</Label>
                          <Input
                            placeholder="Yamaha, Honda..."
                            value={formData.bike_make}
                            onChange={(e) => setFormData(prev => ({ ...prev, bike_make: e.target.value }))}
                            className={`h-10 ${bikeFormAttempted && !formData.bike_make ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                            data-testid="bike-make"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Model' : 'মডেল'} *</Label>
                          <Input
                            placeholder="FZ-S, CBR..."
                            value={formData.bike_model}
                            onChange={(e) => setFormData(prev => ({ ...prev, bike_model: e.target.value }))}
                            className={`h-10 ${bikeFormAttempted && !formData.bike_model ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                            data-testid="bike-model"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Year' : 'বছর'}</Label>
                          <Input
                            type="number"
                            placeholder="2024"
                            value={formData.bike_year || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, bike_year: e.target.value }))}
                            className="h-10"
                            data-testid="bike-year"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Reg. Number' : 'রেজি. নম্বর'}</Label>
                          <Input
                            placeholder="LA-60-4240 or 60-4240"
                            value={formData.bike_registration}
                            onChange={(e) => setFormData(prev => ({ ...prev, bike_registration: formatRegistrationNumber(e.target.value) }))}
                            className="h-10"
                            data-testid="bike-reg"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Color' : 'রঙ'}</Label>
                          <Input
                            placeholder="Black"
                            value={formData.bike_color || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, bike_color: e.target.value }))}
                            className="h-10"
                            data-testid="bike-color"
                          />
                        </div>
                      </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Services Selection */}
          <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  {t('services_selected')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Services */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={language === 'en' ? 'Search services...' : 'সার্ভিস খুঁজুন...'}
                    value={serviceSearch}
                    onChange={(e) => {
                      setServiceSearch(e.target.value);
                      fetchServices(e.target.value);
                    }}
                    className="pl-10 pr-10"
                  />
                  {serviceSearch && (
                    <button
                      onClick={() => {
                        setServiceSearch('');
                        fetchServices('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Services Grid */}
                {services.length > 0 ? (
                  <div className={`grid grid-cols-2 md:grid-cols-${services.length > 15 ? '4' : '3'} gap-3`}>
                    {services.map((service) => (
                      <div
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          formData.services.includes(service.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`service-${service.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{language === 'bn' && service.name_bn ? service.name_bn : service.name}</p>
                            <p className="text-xs text-slate-500">{service.estimated_time}</p>
                          </div>
                          <div onClick={(e) => { e.stopPropagation(); }} className="ml-2">
                            {editingServicePrice === service.id ? (
                              <Input
                                type="text"
                                value={Math.round(formData.service_prices?.[service.id] ?? service.price)}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  updateServicePrice(service.id, value);
                                }}
                                onBlur={() => setEditingServicePrice(null)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditingServicePrice(null)}
                                className="w-20 h-7 text-xs"
                                autoFocus
                              />
                            ) : (
                              <button
                                onClick={() => setEditingServicePrice(service.id)}
                                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-200 transition-colors group"
                              >
                                <span className="text-sm font-bold text-blue-800">{formatCurrency(formData.service_prices?.[service.id] ?? service.price, language)}</span>
                                <Pencil className="w-3 h-3 text-slate-400 group-hover:text-blue-800 transition-colors" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4">
                    {language === 'en' ? 'No services found' : 'কোন সার্ভিস পাওয়া যায়নি'}
                  </p>
                )}

                {/* Custom Service (Mojuri) Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-700">
                      {language === 'en' ? 'Custom Service (Mojuri)' : 'কাস্টম সার্ভিস (মজুরি)'}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowServiceCost(!showServiceCost)}
                      className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showServiceCost ? (language === 'en' ? 'Hide cost' : 'খরচ লুকান') : (language === 'en' ? '+ Add cost' : '+ খরচ যোগ')}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder={language === 'en' ? 'Service name' : 'সার্ভিসের নাম'}
                      value={customService.name}
                      onChange={(e) => setCustomService(prev => ({ ...prev, name: e.target.value }))}
                      className="flex-1"
                    />
                    {showServiceCost && (
                      <Input
                        type="text"
                        placeholder={language === 'en' ? 'Cost' : 'খরচ'}
                        value={customService.cost}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setCustomService(prev => ({ ...prev, cost: value }));
                        }}
                        className="w-24"
                      />
                    )}
                    <Input
                      type="text"
                      placeholder={language === 'en' ? 'Price' : 'মূল্য'}
                      value={customService.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setCustomService(prev => ({ ...prev, price: value }));
                      }}
                      className="w-24"
                    />
                    <Button
                      type="button"
                      onClick={addCustomService}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Custom Services List */}
                  {formData.custom_services && formData.custom_services.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.custom_services.map((cs) => (
                        <div key={cs.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                          <div>
                            <p className="font-medium text-sm">{cs.name}</p>
                            <p className="text-xs text-slate-500">{cs.cost > 0 ? `${language === 'en' ? 'Cost' : 'খরচ'}: ${formatCurrency(cs.cost, language)}` : (language === 'en' ? 'Custom' : 'কাস্টম')}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-800">{formatCurrency(cs.price, language)}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCustomService(cs.id)}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          

          {/* Parts Needed */}
          <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('parts_needed')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selected Parts */}
                {formData.parts_used.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.parts_used.map((part) => (
                      <div key={part.part_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium">{part.part_name}</p>
                            {partCategoryMap[part.part_id] && !partCategoryMap[part.part_id].is_non_consumable && partCategoryMap[part.part_id].name?.toLowerCase() !== 'not consumable' && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-slate-400 bg-slate-50 rounded-full border border-slate-200">
                                <Bell className="w-3 h-3" />
                                {partCategoryMap[part.part_id].days || 90}d
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{formatCurrency(part.unit_price, language)} each</p>
                        </div>
                        <div className="flex items-center justify-between gap-3 flex-1">
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => updatePartQuantity(part.part_id, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="font-bold w-8 text-center">{part.quantity}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => updatePartQuantity(part.part_id, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Editable Total Price - Right Aligned */}
                          <div className="w-32">
                            {editingPartPrice === part.part_id ? (
                              <Input
                                type="text"
                                value={Math.round(part.quantity * part.unit_price)}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  updatePartPrice(part.part_id, value);
                                }}
                                onBlur={() => setEditingPartPrice(null)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditingPartPrice(null)}
                                className="w-full h-8 text-sm"
                                autoFocus
                                data-testid={`part-price-${part.part_id}`}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => setEditingPartPrice(part.part_id)}
                                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-200 transition-colors group ml-auto"
                              >
                                <span className="font-bold text-blue-800">{formatCurrency(part.quantity * part.unit_price, language)}</span>
                                <Pencil className="w-3 h-3 text-slate-400 group-hover:text-blue-800 transition-colors" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search Parts */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={language === 'en' ? 'Search by name, SKU, brand...' : 'নাম, SKU, ব্র্যান্ড দিয়ে খুঁজুন...'}
                    value={partSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPartSearch(val);
                      clearTimeout(window._partSearchTimer);
                      window._partSearchTimer = setTimeout(() => fetchParts(val), 300);
                    }}
                    className="pl-10 pr-10"
                  />
                  {partSearch && (
                    <button
                      onClick={() => {
                        setPartSearch('');
                        fetchParts('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Parts Grid */}
                {partSearch ? (
                  // Show search results
                  parts.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {parts.map((part) => (
                        <Button
                          key={part.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addPart(part)}
                          className="justify-start text-left h-auto py-2"
                          data-testid={`add-part-${part.id}`}
                        >
                          <div className="truncate w-full">
                            <p className="text-xs truncate font-medium">{part.name}</p>
                            <p className="text-xs text-slate-500">{formatCurrency(part.selling_price, language)}</p>
                            <p className="text-[10px] text-slate-400 truncate">
                              {part.brand && <span>{part.brand}</span>}
                              {part.brand && part.stock_quantity != null && <span> · </span>}
                              {part.stock_quantity != null && <span>{language === 'en' ? 'Stock' : 'স্টক'}: {part.stock_quantity}</span>}
                              {partCategoryMap[part.id] && !partCategoryMap[part.id].is_non_consumable && partCategoryMap[part.id].name?.toLowerCase() !== 'not consumable' && <span>{(part.brand || part.stock_quantity != null) ? ' · ' : ''}<Bell className="inline w-[3px] h-[3px]" /> {partCategoryMap[part.id].days || 90}d</span>}
                            </p>
                          </div>
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">
                      {language === 'en' ? 'No parts found' : 'কোন পার্টস পাওয়া যায়নি'}}
                    </p>
                  )
                ) : (
                  // Show recent parts when no search
                  recentParts.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-2">
                        {language === 'en' ? 'Recently used parts' : 'সম্প্রতি ব্যবহৃত পার্টস'}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {recentParts.map((part) => (
                          <Button
                            key={part.id}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addPart(part)}
                            className="justify-start text-left h-auto py-2"
                            data-testid={`add-part-${part.id}`}
                          >
                            <div className="truncate w-full">
                              <p className="text-xs truncate font-medium">{part.name}</p>
                              <p className="text-xs text-slate-500">{formatCurrency(part.selling_price, language)}</p>
                              <p className="text-[10px] text-slate-400 truncate">
                                {part.brand && <span>{part.brand}</span>}
                                {part.brand && part.stock_quantity != null && <span> · </span>}
                                {part.stock_quantity != null && <span>{language === 'en' ? 'Stock' : 'স্টক'}: {part.stock_quantity}</span>}
                                {partCategoryMap[part.id] && !partCategoryMap[part.id].is_non_consumable && partCategoryMap[part.id].name?.toLowerCase() !== 'not consumable' && <span>{(part.brand || part.stock_quantity != null) ? ' · ' : ''}<Bell className="inline w-[3px] h-[3px]" /> {partCategoryMap[part.id].days || 90}d</span>}
                              </p>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </CardContent>
            </Card>

          {/* Custom Parts (not in inventory) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {language === 'en' ? 'Custom Parts (Not in Inventory)' : 'কাস্টম পার্টস (ইনভেন্টরিতে নেই)'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder={language === 'en' ? 'Part name' : 'পার্টসের নাম'}
                  value={customPart.name}
                  onChange={(e) => setCustomPart(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1"
                />
                <Input
                  type="text"
                  placeholder={language === 'en' ? 'Buying ৳' : 'ক্রয় ৳'}
                  value={customPart.buying_price}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCustomPart(prev => ({ ...prev, buying_price: value }));
                  }}
                  className="w-24"
                />
                <Input
                  type="text"
                  placeholder={language === 'en' ? 'Selling ৳' : 'বিক্রয় ৳'}
                  value={customPart.selling_price}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCustomPart(prev => ({ ...prev, selling_price: value }));
                  }}
                  className="w-24"
                />
                <Button type="button" onClick={addCustomPart} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.custom_parts && formData.custom_parts.length > 0 && (
                <div className="space-y-2">
                  {formData.custom_parts.map((cp) => (
                    <div key={cp.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cp.name}</p>
                        <p className="text-xs text-slate-500">
                          {language === 'en' ? 'Buy' : 'ক্রয়'}: {formatCurrency(cp.buying_price, language)} · {language === 'en' ? 'Sell' : 'বিক্রয়'}: {formatCurrency(cp.selling_price, language)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => updateCustomPartQuantity(cp.id, -1)}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="font-bold w-8 text-center">{cp.quantity || 1}</span>
                          <Button type="button" size="sm" variant="outline" onClick={() => updateCustomPartQuantity(cp.id, 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <span className="font-bold text-amber-800">{formatCurrency((cp.quantity || 1) * cp.selling_price, language)}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomPart(cp.id)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card>
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Additional Info' : 'অতিরিক্ত তথ্য'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {technicians.length > 0 && (
                <div>
                  <Label>{t('technician')}</Label>
                  <Select
                    value={formData.technician_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, technician_id: value }))}
                  >
                    <SelectTrigger data-testid="technician-select">
                      <SelectValue placeholder={language === 'en' ? 'Assign technician...' : 'টেকনিশিয়ান নির্বাচন করুন...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {technicians.map((tech) => (
                        <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>{t('notes')}</Label>
                <Textarea
                  placeholder={language === 'en' ? 'Special instructions or notes...' : 'বিশেষ নির্দেশনা বা নোট...'}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  data-testid="job-notes"
                />
              </div>

              <div>
                <Label>{t('expected_delivery')}</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery: e.target.value }))}
                  data-testid="expected-delivery"
                />
              </div>
            </CardContent>
          </Card>

          {/* Summary & Submit */}
          <Card className="bg-slate-900 text-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-slate-400 mb-1">{language === 'en' ? 'Total Amount' : 'মোট পরিমাণ'}</p>
                  <div className="flex items-baseline gap-4">
                    <p className="text-3xl font-bold">{formatCurrency(totals.total, language)}</p>
                    <p className="text-sm text-slate-400">
                      ({language === 'en' ? 'Services' : 'সার্ভিস'}: {formatCurrency(totals.servicesTotal, language)} + 
                      {language === 'en' ? ' Parts' : ' পার্টস'}: {formatCurrency(totals.partsTotal, language)})
                    </p>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 h-14 px-8"
                  disabled={loading || totals.total === 0}
                  data-testid="submit-job"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : !formData.customer_id && totals.total > 0 ? (
                    language === 'en' ? 'Create Quick Entry' : 'কুইক এন্ট্রি তৈরি করুন'
                  ) : (
                    language === 'en' ? 'Create Job' : 'জব তৈরি করুন'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </AdminLayout>
  );
}
